import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useMessageNotifications } from 'contexts/MessageNotificationContext';
import Navbar from 'components/Header/Navbar';
import Footer from 'components/Footer';
import ChatPanel from 'components/support/ChatPanel';
import ListingContextBanner from 'components/chat/ListingContextBanner';
import BuyerVerifyButton from 'components/Listing/BuyerVerifyButton';
import RatingModal from 'components/Rating/RatingModal';
import { conversationService } from 'api/services/ConversationService';
import { userService } from 'api/services/UserService';
import { moderationService } from 'api/services/ModerationService';
import type { ConversationViewModel } from 'api/types/conversation/ConversationViewModel';
import '@fortawesome/fontawesome-free/css/all.min.css';

interface ConversationListItem extends ConversationViewModel {
  otherParticipantName: string;
}

const LIST_REFETCH_INTERVAL_MS = 30000;

const Mesajlar: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkConvId = searchParams.get('conversationId');
  const { user } = useAuth();
  const { markConversationRead, setBadgeExcludeConversationId, refresh: refreshMessageNotifications } =
    useMessageNotifications();
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<ConversationListItem | null>(null);
  const [ratingTarget, setRatingTarget] = useState<{ soldListingId: string; listingTitle: string } | null>(null);

  const loadConversations = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await conversationService.getMyConversations({
        pageNumber: 1,
        pageSize: 50,
        withUnread: true,
      });
      const list = (result.data ?? []) as ConversationViewModel[];
      const peerIds = new Set<string>();
      for (const c of list) {
        const otherId = c.senderId === user.id ? c.receiverId : c.senderId;
        if (otherId) peerIds.add(otherId);
      }
      const nameMap: Record<string, string> = {};
      for (const uid of Array.from(peerIds)) {
        try {
          const u = await userService.getById(uid);
          const uu = u.data;
          nameMap[uid] = uu
            ? [uu.firstName, uu.lastName].filter(Boolean).join(' ') || uu.email || uid
            : uid;
        } catch {
          nameMap[uid] = uid;
        }
      }
      const withNames: ConversationListItem[] = list.map((c) => {
        const otherId = c.senderId === user.id ? c.receiverId : c.senderId;
        return {
          ...c,
          otherParticipantName: otherId ? nameMap[otherId] ?? otherId : 'Kullanıcı',
        };
      });
      let blockedIds = new Set<string>();
      try {
        const blockedRes = await moderationService.getBlockedUserIds();
        if (blockedRes.isSuccess && blockedRes.data?.length) {
          blockedIds = new Set(blockedRes.data.map(String));
        }
      } catch {
        /* list still useful without block filter */
      }
      const withoutBlocked = withNames.filter((c) => {
        const otherId = c.senderId === user.id ? c.receiverId : c.senderId;
        if (!otherId) return true;
        return !blockedIds.has(String(otherId));
      });
      setConversations(withoutBlocked);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Konuşmalar yüklenemedi.');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      loadConversations();
    } else {
      setLoading(false);
      navigate('/giris', { replace: true });
    }
  }, [user?.id, navigate, loadConversations]);

  useEffect(() => {
    if (!user?.id) return;
    const interval = setInterval(loadConversations, LIST_REFETCH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user?.id, loadConversations]);

  useEffect(() => {
    if (!selected) return;
    const stillThere = conversations.some((c) => c.id === selected.id);
    if (!stillThere) setSelected(null);
  }, [conversations, selected]);

  // Deep-link: open the conversation identified by the `?conversationId=…` query string
  // as soon as it's available in the loaded list.
  useEffect(() => {
    if (!deepLinkConvId || selected?.id === deepLinkConvId) return;
    const match = conversations.find((c) => c.id === deepLinkConvId);
    if (match) setSelected(match);
  }, [deepLinkConvId, conversations, selected?.id]);

  // Drive the header letter-icon badge down when a conversation is opened.
  useEffect(() => {
    if (!selected?.id) return;
    void markConversationRead(selected.id);
  }, [selected?.id, markConversationRead]);

  // Header aggregate unread excludes the conversation currently open (per spec 012).
  useEffect(() => {
    setBadgeExcludeConversationId(selected?.id ?? null);
    void refreshMessageNotifications();
  }, [selected?.id, setBadgeExcludeConversationId, refreshMessageNotifications]);

  const filtered = searchQuery.trim()
    ? conversations.filter((c) =>
        c.otherParticipantName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : conversations;

  if (!user) return null;

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
      <Navbar />

      <main className="container-fluid px-2 px-md-3 py-3">
        <div className="card message-app-container shadow-sm overflow-hidden">
          <div className="row g-0 flex-md-row flex-column" style={{ minHeight: 'calc(100vh - 180px)' }}>
            {/* Sol - Konuşma listesi */}
            <div
              className={`col-12 col-md-4 border-end d-flex flex-column ${
                selected ? 'd-none d-md-flex' : ''
              }`}
              style={{ minHeight: 200, maxHeight: 'calc(100vh - 180px)', backgroundColor: 'var(--sb-bg)' }}
            >
              <div className="chat-header p-3 border-bottom flex-shrink-0">
                <h5 className="mb-0">Mesajlarım</h5>
              </div>
              <div className="p-2 p-md-3 border-bottom flex-shrink-0">
                <div className="input-group input-group-sm">
                  <span className="input-group-text border-end-0" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
                    <i className="fas fa-search text-muted" />
                  </span>
                  <input
                    className="form-control border-start-0"
                    style={{ backgroundColor: 'var(--sb-bg-muted)' }}
                    placeholder="Mesajlarda ara..."
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="overflow-auto flex-grow-1">
                {loading ? (
                  <div className="d-flex align-items-center justify-content-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Yükleniyor...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="p-3 text-danger small">{error}</div>
                ) : filtered.length > 0 ? (
                  filtered.map((conv) => (
                    <button
                      key={conv.id}
                      type="button"
                      onClick={() => setSelected(conv)}
                      className={`chat-user w-100 p-3 border-bottom border-start-0 border-end-0 text-start text-dark bg-transparent ${
                        selected?.id === conv.id ? 'active' : ''
                      }`}
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center flex-shrink-0 me-3"
                          style={{ width: 44, height: 44 }}
                        >
                          <i className="fas fa-user" />
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <h6 className="mb-0 text-truncate">{conv.otherParticipantName}</h6>
                          <small className="text-muted">
                            {conv.isClosed ? 'Sohbet kapatıldı' : 'Aktif sohbet'}
                          </small>
                        </div>
                        <i className="fas fa-chevron-right text-muted small" />
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="text-center p-4">
                    <i className="far fa-comments fa-2x text-muted mb-3" />
                    <p className="mb-0 text-muted small">
                      {searchQuery.trim() ? 'Aramanızla eşleşen konuşma yok.' : 'Henüz konuşmanız yok.'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Sağ - Sohbet veya boş durum. When selected: fixed height so flex chain works and input stays at bottom. */}
            <div
              className="col-12 col-md-8 d-flex flex-column chat-right-column"
              style={
                selected
                  ? { height: 'calc(100vh - 180px)', minHeight: 320, backgroundColor: 'var(--sb-bg-muted)' }
                  : { minHeight: 280, backgroundColor: 'var(--sb-bg-muted)' }
              }
            >
              {selected ? (
                <>
                  <div className="p-2 p-md-3 border-bottom flex-shrink-0 d-flex align-items-center gap-2" style={{ backgroundColor: 'var(--sb-bg)' }}>
                    <button
                      type="button"
                      className="btn btn-link text-muted text-decoration-none p-0 me-2 d-md-none"
                      onClick={() => setSelected(null)}
                      aria-label="Listeye dön"
                    >
                      <i className="fas fa-arrow-left" />
                    </button>
                    <div className="flex-grow-1 min-width-0">
                      <h6 className="mb-0 text-truncate">{selected.otherParticipantName}</h6>
                    </div>
                  </div>
                  <ListingContextBanner summary={selected.listingSummary ?? null} />
                  {selected.listingSummary?.id && (selected.listingSummary.soldListing ?? null) && (
                    <div className="px-2 px-md-3 pt-2 d-flex flex-wrap gap-2">
                      <BuyerVerifyButton
                        listingId={selected.listingSummary.id}
                        currentUserId={user.id}
                        soldListingSummary={selected.listingSummary.soldListing ?? null}
                        onVerified={(updated) =>
                          setSelected((prev) =>
                            prev && prev.listingSummary
                              ? {
                                  ...prev,
                                  listingSummary: {
                                    ...prev.listingSummary,
                                    soldListing: updated,
                                  },
                                }
                              : prev,
                          )
                        }
                      />
                      {selected.listingSummary.soldListing
                        && user.id === selected.listingSummary.soldListing.soldToUserId
                        && selected.listingSummary.soldListing.completedAt != null
                        && !selected.listingSummary.soldListing.hasRating && (
                        <button
                          type="button"
                          className="btn btn-warning"
                          onClick={() => setRatingTarget({
                            soldListingId: selected.listingSummary!.soldListing!.id,
                            listingTitle: selected.listingSummary!.title,
                          })}
                        >
                          <i className="fas fa-star me-2" />
                          Satıcıyı Değerlendir
                        </button>
                      )}
                    </div>
                  )}
                  <div className="flex-grow-1 min-height-0 p-2 p-md-3 d-flex flex-column overflow-hidden">
                    <ChatPanel
                      key={selected.id}
                      conversationId={selected.id}
                      currentUserId={user.id}
                      isReadOnly={selected.isClosed ?? false}
                      title=""
                      fillHeight
                    />
                  </div>
                </>
              ) : (
                <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 py-5 px-3">
                  <i className="far fa-comment-dots fa-4x text-muted mb-3" />
                  <h5 className="mb-2">Mesajlarınız</h5>
                  <p className="text-muted text-center mb-4 small">
                    Soldan bir konuşma seçin veya bir ilandan &quot;Mesaj Gönder&quot; ile yeni konuşma başlatın.
                  </p>
                  <Link className="btn btn-primary btn-sm" to="/">
                    <i className="fas fa-search me-2" />
                    İlanlara Göz At
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .message-app-container { border-radius: 0.5rem; }
        .chat-user:hover { background-color: var(--sb-bg-muted) !important; }
        .chat-user.active { background-color: var(--sb-dropdown-hover-bg) !important; border-left: 3px solid #0d6efd; }
        .chat-user:focus { outline: none; box-shadow: none; }
        .chat-right-column { position: relative; }
        .chat-input-form { position: sticky; bottom: 0; z-index: 2; }
        @media (min-width: 768px) {
          .message-app-container { min-height: calc(100vh - 180px); }
        }
        @media (max-width: 767.98px) {
          .chat-right-column.chat-right-column { height: calc(100vh - 140px) !important; min-height: 280px; }
        }
      `}</style>
      {ratingTarget && (
        <RatingModal
          soldListingId={ratingTarget.soldListingId}
          listingTitle={ratingTarget.listingTitle}
          onSubmitted={() =>
            setSelected((prev) =>
              prev && prev.listingSummary?.soldListing
                ? {
                    ...prev,
                    listingSummary: {
                      ...prev.listingSummary,
                      soldListing: { ...prev.listingSummary.soldListing, hasRating: true },
                    },
                  }
                : prev,
            )
          }
          onClose={() => setRatingTarget(null)}
        />
      )}
      <Footer />
    </div>
  );
};

export default Mesajlar;
